import { catalog } from './catalog.js';
import { db } from './db.js';
import { FilesClient, confidentMatch } from './files.js';
import { captureFrame, readableOrigin } from './video-frame.js';

/* Carátulas que el proveedor no trae —o que trae pero no se pueden cargar (dirección caída, 404, servidor al que la web no
   llega)—: cuando una tarjeta sin imagen entra en pantalla, o su imagen falla, se le pide la de TMDB (por título y año).
   Solo lo que se ve, de pocas en pocas. Lo encontrado (y lo que no existe) se guarda en el dispositivo por proveedor, así la
   próxima vez salen al momento y sin volver a preguntar; la que sustituye a una rota se aplica al cargar el catálogo. */

const MAX_PARALLEL = 3;
const MAX_FRAMES = 2; // fotogramas de vídeos propios a la vez: cada uno baja un trozo del vídeo (si está en la nube, de internet)
const FRAME_MISS_TTL = 86400000; // un vídeo que no se pudo decodificar se vuelve a probar al día siguiente
const MISS_TTL = 7 * 86400000; // lo que TMDB no conoce no se vuelve a preguntar en una semana
const SAVE_DELAY_MS = 2500;

const queue = [];
const frameQueue = [];
const queued = new Set();
let active = 0;
let frameActive = 0;
let map = null;     // «kind:id» → { p: poster, t: marca de tiempo } ({ p: '' } = TMDB no lo conoce)
let mapFor = '';
let loading = null;
let saveTimer = null;

/** Quien pinta las tarjetas se registra aquí para poner la imagen en las que ya están en pantalla. */
export const posterFill = {
    onPoster: null,
    request,
    setPoster,
    preload: () => load().then(applyOverrides),
    pending: () => queue.length + active + frameQueue.length + frameActive
};

async function load() {
    const key = 'posters:' + catalog.id;
    if (map && mapFor === key) return map;
    if (loading && mapFor === key) return loading;
    mapFor = key;
    loading = db.get('meta', key).catch(() => null).then((m) => { map = m && typeof m === 'object' ? m : {}; loading = null; return map; });
    return loading;
}

function save() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => { if (map) db.put('meta', mapFor, map).catch(() => {}); }, SAVE_DELAY_MS);
}

function apply(kind, id, poster, { force = false } = {}) {
    const item = catalog.get(kind, id);
    if (!item || !poster || item.poster === poster || (item.poster && !force)) return;
    item.poster = poster;
    if (posterFill.onPoster) posterFill.onPoster(kind, id, poster);
}

/** Las carátulas de TMDB que sustituyen a otras rotas se ponen en el catálogo en cuanto se conoce, antes de pintar nada. */
function applyOverrides() {
    if (!map) return;
    Object.keys(map).forEach((key) => {
        const e = map[key];
        if (!e || !e.o || !e.p) return;
        const [kind, id] = key.split(':');
        const item = catalog.get(kind, id);
        if (item && item.poster !== e.p) item.poster = e.p;
    });
}

/** El servidor local de la app se lee directo; lo demás, por donde se reproduciría (en la web, el proxy del mismo origen deja leer los píxeles). */
const streamOf = (raw, played) => (readableOrigin(raw) ? raw : played) || '';

/** La dirección de un vídeo propio (carpeta, SMB, nube…) del que sacar un fotograma, o '' si no es de los que se pueden. */
export function frameSource(kind, item) {
    if (!item || item.poster) return '';
    let url = '';
    if (kind === 'vod') {
        const client = catalog._client(item);
        if (client instanceof FilesClient) url = streamOf(catalog.rawVodUrl(item), catalog.vodUrl(item));
    } else if (kind === 'series') {
        const client = catalog._client(item);
        const eps = [].concat(...((item.seasons || []).map((s) => s.episodes || [])));
        if (client instanceof FilesClient && eps[0]) url = streamOf(catalog.rawEpisodeUrl(eps[0]), catalog.episodeUrl(eps[0]));
    }
    // Con usuario y contraseña dentro de la dirección, el navegador no deja cargarlo como vídeo de la página.
    return url && !/^https?:\/\/[^/@]*@/.test(url) ? url : '';
}

async function workFrames() {
    while (frameActive < MAX_FRAMES && frameQueue.length) {
        const job = frameQueue.shift();
        frameActive++;
        runFrame(job).finally(() => { frameActive--; queued.delete(job.kind + ':' + job.id); workFrames(); });
    }
}

async function runFrame({ kind, id }) {
    const item = catalog.get(kind, id);
    if (!item || item.poster) return;
    const url = frameSource(kind, item);
    if (!url) return;
    const frame = await captureFrame(url);
    map[kind + ':' + id] = { p: frame, t: Date.now(), f: 1 };
    save();
    if (frame) apply(kind, id, frame);
}

async function work() {
    while (active < MAX_PARALLEL && queue.length) {
        const { kind, id, broken } = queue.shift();
        active++;
        run(kind, id, broken).finally(() => { active--; queued.delete(kind + ':' + id); work(); });
    }
}

async function run(kind, id, broken) {
    const item = catalog.get(kind, id);
    const enrich = catalog.enricher;
    if (!item || (item.poster && !broken) || !enrich) return;
    const policy = catalog._tmdbPolicy(item);
    if (policy === 'skip') return;
    let x = null;
    try {
        x = await enrich(kind, item);
    } catch (e) {
        return; // sin red o sin cupo: se volverá a intentar la próxima vez que se vea
    }
    if (!x) return;
    const found = (x.tmdbId && x.poster && (policy === 'free' || confidentMatch(item, x)) && x.poster) || '';
    map[kind + ':' + id] = broken ? { p: found, t: Date.now(), o: found ? 1 : 0 } : { p: found, t: Date.now() };
    save();
    if (found) apply(kind, id, found, { force: !!broken });
}

/** Pone una carátula elegida a mano (p. ej. otro fotograma del vídeo): sustituye a la que hubiera y se recuerda. */
async function setPoster(kind, id, poster) {
    await load();
    map[kind + ':' + id] = { p: poster, t: Date.now(), o: 1, f: 1 };
    save();
    apply(kind, id, poster, { force: true });
}

/** Pide la carátula de TMDB para un título sin ella (se llama al entrar en pantalla su tarjeta). */
async function request(kind, id, { broken = false } = {}) {
    if (kind !== 'vod' && kind !== 'series') return;
    const key = kind + ':' + id;
    const item = catalog.get(kind, id);
    const frame = !broken && frameSource(kind, item);
    if (!item || (item.poster && !broken) || queued.has(key) || (!catalog.enricher && !frame)) return;
    const policy = catalog._tmdbPolicy(item);
    if (policy === 'skip' && !frame) return; // vídeos personales: la carátula de TMDB sería la de otra película
    const known = await load();
    // Carpetas propias: lo que TMDB puso antes de comprobar el parecido del título no vale (los fotogramas, que llevan `f`, sí).
    const hit = policy !== 'free' && known[key] && !known[key].f ? undefined : known[key];
    if (hit && hit.p) { apply(kind, id, hit.p, { force: broken }); return; }
    if (hit && Date.now() - hit.t < (hit.f ? FRAME_MISS_TTL : MISS_TTL)) return;
    if (queued.has(key)) return;
    queued.add(key);
    // Un vídeo propio no tiene ficha que buscar: su carátula es un fotograma suyo (los de TMDB ya se intentaron al leer la carpeta).
    if (frame) { frameQueue.push({ kind, id }); workFrames(); return; }
    queue.push({ kind, id, broken });
    work();
}

// Al cambiar el catálogo (otro proveedor, recarga) se reaplican las carátulas guardadas que sustituyen a las rotas.
let overrideTimer = null;
catalog.on('change', () => {
    clearTimeout(overrideTimer);
    overrideTimer = setTimeout(() => { if (catalog.loaded) posterFill.preload().catch(() => {}); }, 400);
});
